import rootStore from '@vue-storefront/core/store'
import type { Store } from 'vuex'

/** Main-nav columns in Magento (level 1 = root). */
export const MEGAMENU_TOP_LEVEL = 2

/** Tree structure, links, sidebar; `image` for megamenu column thumbnails. */
export const MEGAMENU_NAV_FIELDS = [
  'id',
  'parent_id',
  'level',
  'position',
  'name',
  'name_alias',
  'url_path',
  'url_key',
  'image',
  'children_count',
  'include_in_menu',
  'is_active',
  'product_count'
] as const

/** Megamenu column-3 detail card (loaded per hovered category). */
export const MEGAMENU_DETAIL_FIELDS = [
  ...MEGAMENU_NAV_FIELDS,
  'image',
  'pulldowntext',
  'pulldowntitle'
] as const

/** Category sliders / height selector tiles on category pages. */
export const MENU_SLIDER_FIELDS = [
  'id',
  'parent_id',
  'level',
  'position',
  'name',
  'name_alias',
  'url_path',
  'url_key',
  'image',
  'zusatzzeile',
  'category_linktext',
  'sale_label',
  'children_count',
  'product_count'
] as const

export function getMegamenuNavIncludeFields (): string[] {
  return [...MEGAMENU_NAV_FIELDS]
}

export function getMegamenuDetailIncludeFields (): string[] {
  return [...MEGAMENU_DETAIL_FIELDS]
}

/** @deprecated use getMenuSliderIncludeFields */
export function getMenuCategoryIncludeFields (): string[] {
  return getMenuSliderIncludeFields()
}

export function getMenuSliderIncludeFields (): string[] {
  return [...MENU_SLIDER_FIELDS]
}

export function menuChildrenLoaded (
  store: Store<any>,
  parentId: number | string,
  { withImage = false }: { withImage?: boolean } = {}
): boolean {
  const pid = Number(parentId)
  if (!pid) {
    return false
  }
  const state = store.state['category-next'] || {}
  const loadedParents: Array<number | string> = state.menuChildrenLoadedParentIds || []
  // Homepage / slider id-fetches leave some children in menuCategories without a
  // full parent_id query — only treat as complete after an explicit children fetch.
  if (!loadedParents.some((id) => Number(id) === pid)) {
    return false
  }
  if (!withImage) {
    return true
  }
  const children = (state.menuCategories || []).filter(
    (c) => Number(c.parent_id) === pid
  )
  // Refetch only when `image` was omitted from a prior lean payload — not when
  // Magento has no image (null / empty string).
  return children.every((c) => Object.prototype.hasOwnProperty.call(c, 'image'))
}

export function menuCategoriesLoaded (store: Store<any>, ids: Array<number | string>): boolean {
  const menu = store.state['category-next'].menuCategories || []
  return ids.every((id) => menu.some((c) => Number(c.id) === Number(id)))
}

export function loadMenuCategoryChildren (parentId: number | string) {
  return rootStore.dispatch('category-next/fetchMenuCategories', {
    parent: parentId,
    includeFields: getMegamenuNavIncludeFields(),
    onlyActive: true,
    onlyNotEmpty: false
  })
}

export function loadMenuCategoriesByIds (ids: Array<number | string>) {
  const value = [...ids]
  if (!value.length) {
    return Promise.resolve()
  }
  return rootStore.dispatch('category-next/fetchMenuCategories', {
    key: 'id',
    value,
    includeFields: getMenuSliderIncludeFields()
  })
}

export function loadMenuCategoryDetails (id: number | string) {
  return rootStore.dispatch('category-next/fetchMenuCategories', {
    key: 'id',
    value: [id],
    includeFields: getMegamenuDetailIncludeFields()
  })
}

/**
 * Top-level nav only (~9 categories). Deeper levels load on hover / per page.
 */
export function loadMenuCategories () {
  return rootStore.dispatch('category-next/fetchMenuCategories', {
    level: MEGAMENU_TOP_LEVEL,
    includeFields: getMegamenuNavIncludeFields(),
    key: 'include_in_menu',
    value: 1,
    size: 50
  })
}

/** SSR: first megamenu column is open by default — prefetch its children once. */
export function prefetchFirstMegamenuColumn () {
  return loadMenuCategories().then((resp) => {
    const items =
      resp?.items ||
      (rootStore.state['category-next'].menuCategories || []).filter(
        (c) => c.level === MEGAMENU_TOP_LEVEL
      )
    const first = [...items].sort((a, b) => a.position - b.position)[0]
    if (first?.children_count > 0 && !menuChildrenLoaded(rootStore, first.id, { withImage: true })) {
      return loadMenuCategoryChildren(first.id)
    }
  })
}

export function ensureMenuCategoryChildren (
  store: Store<any>,
  parentId: number | string,
  { withImage = false }: { withImage?: boolean } = {}
) {
  if (!parentId || menuChildrenLoaded(store, parentId, { withImage })) {
    return Promise.resolve()
  }
  return loadMenuCategoryChildren(parentId)
}

/** Megamenu nav fetches omit slider tile copy (`zusatzzeile`, `category_linktext`). */
export function menuCategoryNeedsSliderFields (category: Record<string, unknown> | undefined): boolean {
  if (!category) {
    return true
  }
  return !(
    Object.prototype.hasOwnProperty.call(category, 'zusatzzeile') ||
    Object.prototype.hasOwnProperty.call(category, 'category_linktext')
  )
}

export function ensureMenuCategoriesByIds (store: Store<any>, ids: Array<number | string>) {
  const menu = store.state['category-next'].menuCategories || []
  const needsFetch = ids.filter((id) => {
    const existing = menu.find((c) => Number(c.id) === Number(id))
    return menuCategoryNeedsSliderFields(existing)
  })
  if (!needsFetch.length) {
    return Promise.resolve()
  }
  return loadMenuCategoriesByIds(needsFetch)
}

export function ensureMenuCategoryDetails (store: Store<any>, id: number | string) {
  const self = (store.state['category-next'].menuCategories || []).find(
    (c) => String(c.id) === String(id)
  )
  if (
    self &&
    self.image &&
    Object.prototype.hasOwnProperty.call(self, 'pulldowntext')
  ) {
    return Promise.resolve()
  }
  return loadMenuCategoryDetails(id)
}
